import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'atm_token';
const USER_KEY = 'atm_user';

export type UserRole = 'reader' | 'operator' | 'admin';

export interface AuthUser {
  username: string;
  mode: 'mock' | 'azure';
  role: UserRole;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loginLocal: (username: string, password: string) => Promise<void>;
  loginAzure: (idToken: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function setAxiosToken(token: string | null) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

// Restore token synchronously on module load so first API call is authenticated
const storedToken = localStorage.getItem(TOKEN_KEY);
if (storedToken) {
  setAxiosToken(storedToken);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAxiosToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  const handleAuthResponse = (data: { token: string; username: string; mode: string; role?: string }) => {
    const authUser: AuthUser = {
      username: data.username,
      mode: data.mode as 'mock' | 'azure',
      role: (data.role as UserRole) || 'reader',
    };
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
    setAxiosToken(data.token);
    setUser(authUser);
  };

  const loginLocal = async (username: string, password: string) => {
    const response = await axios.post(`${API_URL}/auth/login`, { username, password });
    handleAuthResponse(response.data);
  };

  const loginAzure = async (idToken: string) => {
    const response = await axios.post(`${API_URL}/auth/azure`, { idToken });
    handleAuthResponse(response.data);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, loginLocal, loginAzure, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
